import { notification } from '@todesktop/client-core';
import { checkPermission } from '@/helpers/checkPermissions';
import { connectPusher, channelIsConnected } from '@/helpers/pusherHelpers';
import * as Sentry from '@sentry/browser';
import {
  handleFolderChange,
  handleAssignmentActivity,
  handleBlockedActivity,
  handleConsent,
} from '../../../cache/conversationsCache';
import ProgressStates from '../../../helpers/lists/progressStates';

// function connectToChannel(channelName) {
//   let channel;
//   const channelKeys = window.pusher.channels && window.pusher.channels.channels ? Object.keys(window.pusher.channels.channels) : [];
//   if (!channelKeys.includes(channelName)) {
//     channel = window.pusher.subscribe(channelName);
//   } else {
//     channel = window.pusher.channels.channels[channelName];
//   }

//   return channel;
// }

// function connectEcho() {
//   if (!window.echo) {
//     const token = localStorage.getItem('pbdToken');

//     const devMode = {};
//     if (process.env.NODE_ENV === 'development') {
//       const devHeader = process.env.VUE_APP_DEV_HEADER.split(':');
//       devMode[devHeader[0]] = devHeader[1];
//     }

//     window.echo = new Echo({
//       auth: {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           ...devMode,
//         },
//       },
//       authEndpoint: `${process.env.VUE_APP_API_URL}/app/broadcasting/auth`,
//       broadcaster: 'pusher',
//       key: process.env.VUE_APP_PUSHER_KEY,
//       cluster: 'eu',
//       encrypted: true,
//       namespace: '',
//     });
//   }
// }

// let inactivityTimeout;
// // Timeout time is double of the pusher update time. Used for reseting import on error or rechecking some old import that was left to be run in the background.
// function timeout(dispatch, payload) {
//   clearTimeout(inactivityTimeout);
//   inactivityTimeout = setTimeout(() => {
//     dispatch('unsubscribeFromImportStats', payload);
//   }, 10000);
// }

// let bulkNotificationMessagesInactivityTimeout;
// // Pusher will update very often so 10s timeout should be enough to catch pusher issue. Used for reseting bulk progress on error or rechecking some old bulk send that was left to be run in the background.
// function bulkNotificationsTimeout(dispatch, payload) {
//   clearTimeout(bulkNotificationMessagesInactivityTimeout);
//   bulkNotificationMessagesInactivityTimeout = setTimeout(() => {
//     dispatch('unsubscribeFromBulkSendStats', payload);
//   }, 10000);
// }

// let exportInactivityTimeout;
// // Timeout time is double of the pusher update time. Used for reseting export on error.
// function exportTimeout(dispatch, payload) {
//   clearTimeout(exportInactivityTimeout);
//   exportInactivityTimeout = setTimeout(() => {
//     dispatch('unsubscribeFromExportStats', payload);
//   }, 10000);
// }

// function removeFromExportList(exportUUID, fileDownloadUrl, merchantId) {
//   window.sbToast.hide(`export-toast-${exportUUID}`);
//   localStorage.setItem(`exportWaiting.${merchantId}`, localStorage.getItem(`exportWaiting.${merchantId}`).replace(new RegExp(`${fileDownloadUrl},?`, 'gi'), '').replace(/,$/, ''));
// }

let loadUserDataTimeout;

/**
 * Method checks if user has permission to access chat to which provided message belongs to.
 * Access is granted if:
 *  - user id on the message is equal to current user id
 *  - current user can see unassigned chats
 *  - current user can see assigned chat to somebody else
 * @param {*} message Message which user is trying to process
 * @param {*} rootState Root state of the app.
 */
function userHasPermissionForConversation(message, rootState, rootGetters) {
  let permission = false;
  let role;

  if (rootGetters['organisation/me']) {
    role = rootGetters['organisation/me'].role;
  }

  if (message.assigned_user_id === rootState.user.id) {
    permission = true;
  }

  if (!permission && !message.assigned_user_id) {
    if (rootState.organisation.currentOrganisation.id === '3b0f220b-db12-4123-8632-8d1fa2e6b208' && (!role || !rootState.merchant.permissionRoles)) {
      Sentry.captureException(`Permission unasigned (${role}) - ${JSON.stringify(rootState.merchant.permissionRoles.list_unassigned_conversations)} : ${JSON.stringify(rootState.merchant.permissionRoles)}`);
    }
    permission = checkPermission('list_unassigned_conversations', rootGetters);
  }

  if (!permission && message.assigned_user_id) {
    if (rootState.organisation.currentOrganisation.id === '3b0f220b-db12-4123-8632-8d1fa2e6b208' && (!role || !rootState.merchant.permissionRoles)) {
      Sentry.captureException(`Permission assigned (${role}) - ${JSON.stringify(rootState.merchant.permissionRoles.list_others_assigned_conversations)} : ${JSON.stringify(rootState.merchant.permissionRoles)}`);
    }
    permission = checkPermission('list_others_assigned_conversations', rootGetters);
  }

  return permission;
}

export default {
  resetPusher({ dispatch, rootState }) {
    const chanelBackup = JSON.parse(JSON.stringify(Object.keys(window.pusher.channels.channels)));
    connectPusher(rootState, true);

    window.pusher.connection.bind('initialized', () => {
      // console.log('initialized from reset');
    });
    window.pusher.connection.bind('connecting', () => {
      // console.log('connecting from reset');
    });
    window.pusher.connection.bind('connected', () => {
      dispatch('reconnectCurrentChannels', chanelBackup);
      dispatch('organisation/forceResync', null, { root: true });
    });
    window.pusher.connection.bind('unavailable', () => {
      // console.log('unavailable from reset');
    });
    window.pusher.connection.bind('failed', () => {
      // console.log('Failed from reset');
    });
    window.pusher.connection.bind('disconnected', () => {
      // console.log('DISconnected from reset');
    });
  },
  reconnectCurrentChannels({ dispatch, rootGetters, rootState }, chanelBackup) {
    const currentOrganisation = rootGetters['organisation/currentOrganisation'];

    dispatch('joinPrivateChannel');

    dispatch('leaveOrganisationChannel', currentOrganisation.id);
    dispatch('joinOrganisationChannel', currentOrganisation.id);

    const channelName = rootState.system && rootState.system.pusherChannels && rootState.system.pusherChannels.focus ? rootState.system.pusherChannels.focus.replace('{organisation_id}', currentOrganisation.id) : null;
    const focusChannel = chanelBackup.find(c => c.name === channelName);
    if (focusChannel) {
      dispatch('joinFocusChannel', { join: currentOrganisation.id });
    }

    chanelBackup.forEach((channel) => {
      dispatch('leaveNumberChannel', channel.split('.')[1]);
      if (channel.startsWith('presence-business-data-web')) {
        dispatch('joinNumberChannel', channel.split('.')[1]);
      }
    });
  },
  reconnectChannel({ commit, dispatch }, channelName) {
    // commit('RECONNECT_CHANNEL');
    if (channelName) {
      window.pusher.unsubscribe(channelName);
    }

    const interval = setInterval(() => {
      if (window.pusher.channels.channels[channelName] && window.pusher.channels.channels[channelName].subscribed) {
        // TODO clear all cache
        dispatch('chat/clearFastReload', null, { root: true });
        // TODO reinitialize app but silently

        // Also reload first page of the current chat
        dispatch('chat/reloadFirstPage', true, { root: true });
        clearInterval(interval);
        return;
      }

      const channel = channelName.split('.');
      if (channel[0] === 'presence-data-web') {
        // dispatch('joinOrganisationChannel', channel[1]);
      } else if (channel[0] === 'presence-focus') {
        window.pusher.subscribe(channelName);
      }
    }, 5000);
  },

  // listenForMerchantActivity({ commit, rootState }, merchantId) {
  //   connectEcho();

  //   window.echo.join(`merchant_activity.${merchantId}`)
  //     .listen('MerchantActivityRegistered', (e) => {
  //       commit('MERCHANT_ACTIVITY_REGISTERED', e);
  //     });
  // },
  // // eslint-disable-next-line no-unused-vars
  // subscribeToPusherChat({ commit }, merchantId) {
  //   connectEcho();
  //   if (!Array.isArray(merchantId)) {
  //     (merchantId = [merchantId]);
  //   }

  //   merchantId.forEach((id) => {
  //     window.echo.join(`merchant_chat.${id}`);
  //   });
  // },
  // // eslint-disable-next-line no-unused-vars
  // unsubscribeFromPusherChat({ commit }, merchantId) {
  //   connectEcho();
  //   if (!Array.isArray(merchantId)) {
  //     (merchantId = [merchantId]);
  //   }

  //   merchantId.forEach((id) => {
  //     window.echo.leave(`merchant_chat.${id}`);
  //   });
  // },
  // turnOffEchoConnection() {
  //   if (window.echo) {
  //     window.echo.disconnect();
  //     window.echo = null;
  //   }
  // },
  // subscribeToImportStats({ commit, dispatch, getters }, payload) {
  //   timeout(dispatch, payload);

  //   dispatch('clearImportStats');
  //   dispatch('changeImportRunningState', true);
  //   localStorage.setItem(`importUUID.${payload.merchantId}`, `${payload.importUUID}`);
  //   connectEcho();
  //   window.echo.join(`contact_import.${payload.importUUID}`)
  //     .listen('ContactsImportProgress', (e) => {
  //       timeout(dispatch, payload);

  //       if (e.error) {
  //         commit('contact/SET_IMPORT_ERROR', e.error, { root: true });
  //       } else {
  //         commit('NEW_IMPORT_STATS_RECEIVED', e);
  //       }

  //       const processed = e.added_contacts + e.failed_contacts;

  //       // Show toast on finished and unsubscribe from pusher import stats channel
  //       if (processed === e.total_contacts) {
  //         dispatch('unsubscribeFromImportStats', payload);

  //         // Only show toast if the import modal is closed
  //         if (!getters.preventImportToastMessage) {
  //           const toastBodyElements = [];
  //           if (e.added_contacts) {
  //             toastBodyElements.push(
  //               window.createElement(
  //                 'p',
  //                 { class: ['text-success'] },
  //                 [
  //                   window.createElement('b-icon-check', { class: ['mr-2'] }),
  //                   `${e.added_contacts} contacts processed`,
  //                 ],
  //               ),
  //             );
  //           }
  //           if (e.failed_contacts) {
  //             toastBodyElements.push(
  //               window.createElement(
  //                 'p',
  //                 { class: ['text-danger'] },
  //                 [
  //                   window.createElement('b-icon-x', { class: ['mr-2'] }),
  //                   `${e.failed_contacts} contacts failed `,
  //                 ],
  //               ),
  //             );
  //           }
  //           const toastMessage = window.createElement(
  //             'p',
  //             { class: ['mb-0'] },
  //             toastBodyElements,
  //           );
  //           window.sbToast.toast([toastMessage], {
  //             title: 'Contact import',
  //             toaster: 'b-toaster-top-right',
  //             noAutoHide: true,
  //             to: 'Contacts',
  //           });
  //         }
  //       }
  //     });
  // },
  // unsubscribeFromImportStats({ dispatch }, payload) {
  //   localStorage.removeItem(`importUUID.${payload.merchantId}`);
  //   connectEcho();
  //   window.echo.leave(`contact_import.${payload.importUUID}`);
  //   dispatch('changeImportRunningState', false);
  // },
  // clearImportStats({ commit }) {
  //   commit('CLEAR_IMPORT_STATS');
  // },
  // changeImportRunningState({ commit }, payload) {
  //   commit('CHANGE_IMPORT_RUNNING_STATE', payload);
  // },
  // preventImportToastMessage({ commit }, payload) {
  //   commit('PREVENT_IMPORT_TOAST_MESSAGE', payload);
  // },
  // clearInactivityTimeut() {
  //   clearTimeout(inactivityTimeout);
  // },
  // // eslint-disable-next-line no-unused-vars
  // turnOffMerchantEventListener({ commit }, merchantId) {
  //   window.echo.leave(`merchant_activity.${merchantId}`);
  //   window.echo.leave(`merchant_messages.${merchantId}`);
  // },
  // subscribeToBulkSendStats({ commit, dispatch }, payload) {
  //   // Local storage name is masked on purpose to not make it so obvious for users that they could just delete it.
  //   // Based on this entry the user will be blocked to initiate another bulk send and will get the notification on finish.
  //   // Also the progress will be shown based on the presence of this local storage entry.
  //   localStorage.setItem(`_c_gm_pf_ns.${payload.merchantId}`, payload.bulkSendUUID);
  //   dispatch('clearBulkProgressStats');
  //   dispatch('changeBulkSendRunningState', true);
  //   bulkNotificationsTimeout(dispatch, payload);

  //   connectEcho();
  //   window.echo.join(`bulk_send_template_whatsapp.${payload.bulkSendUUID}`)
  //     .listen('BulkSendTemplateWhatsappProgress', (e) => {
  //       bulkNotificationsTimeout(dispatch, payload);

  //       if (e.error) {
  //         commit('notification/SET_BULK_SEND_ERROR', e.error, { root: true });
  //         window.sbToast.toast(e.error, {
  //           title: 'Bulk notification error',
  //           toaster: 'b-toaster-top-right',
  //           noAutoHide: true,
  //           variant: 'danger',
  //           to: { name: 'SentNotifications' },
  //         });
  //         return;
  //       }

  //       commit('NEW_BULK_SEND_PROGRESS_RECEIVED', e);

  //       let progress = 0;
  //       progress += e.sent || 0;
  //       progress += e.delivered || 0;
  //       progress += e.read || 0;
  //       progress += e.failed || 0;
  //       // Show toast on finished and unsubscribe from pusher bulk send progress channel
  //       if (e.all === progress) {
  //         dispatch('unsubscribeFromBulkSendStats', payload);

  //         const toastBodyElements = [];
  //         if (e.all) {
  //           toastBodyElements.push(
  //             window.createElement(
  //               'p',
  //               { class: ['text-success'] },
  //               [
  //                 window.createElement('b-icon-check', { class: ['mr-2'] }),
  //                 `${e.all} messages sent`,
  //               ],
  //             ),
  //           );
  //         }
  //         if (e.failed) {
  //           toastBodyElements.push(
  //             window.createElement(
  //               'p',
  //               { class: ['text-danger'] },
  //               [
  //                 window.createElement('b-icon-x', { class: ['mr-2'] }),
  //                 `${e.failed} messages failed`,
  //               ],
  //             ),
  //           );
  //         }
  //         const toastMessage = window.createElement(
  //           'p',
  //           { class: ['mb-0'] },
  //           toastBodyElements,
  //         );
  //         window.sbToast.toast([toastMessage], {
  //           title: 'Campaign sent',
  //           toaster: 'b-toaster-top-right',
  //           noAutoHide: true,
  //           to: { name: 'SentNotifications' },
  //         });
  //       }
  //     });
  // },
  // unsubscribeFromBulkSendStats({ dispatch }, payload) {
  //   localStorage.removeItem(`_c_gm_pf_ns.${payload.merchantId}`);
  //   connectEcho();
  //   window.echo.leave(`bulk_send.${payload.importUUID}`);
  //   dispatch('changeBulkSendRunningState', false);
  // },
  // clearBulkProgressStats({ commit }) {
  //   commit('CLEAR_BULK_PROGRESS_STATS');
  // },
  // changeBulkSendRunningState({ commit }, payload) {
  //   commit('CHANGE_BULK_MESSAGES_RUNNING_STATE', payload);
  // },
  // clearBulkSendInactivityTimeut() {
  //   clearTimeout(bulkNotificationMessagesInactivityTimeout);
  // },
  // subscribeToExportStats({ commit, dispatch }, payload) {
  //   exportTimeout(dispatch, payload);
  //   dispatch('clearExportStats');
  //   dispatch('changeExportRunningState', true);
  //   localStorage.setItem(`exportUUID.${payload.merchantId}`, `${payload.exportUUID}`);

  //   connectEcho();
  //   window.echo.join(`contact_export.${payload.exportUUID}`)
  //     .listen('ContactsExportProgress', (e) => {
  //       exportTimeout(dispatch, payload);

  //       if (e.error) {
  //         commit('contact/SET_EXPORT_ERROR', e.error, { root: true });
  //       } else {
  //         commit('NEW_EXPORT_STATS_RECEIVED', e);
  //       }

  //       // Show toast on finished and unsubscribe from pusher export stats channel
  //       if (e.added_contacts === e.total_contacts) {
  //         dispatch('unsubscribeFromExportStats', payload);
  //         if (window.useMultiExport && localStorage.getItem(`exportWaiting.${payload.merchantId}`)) {
  //           // eslint-disable-next-line prefer-template
  //           localStorage.setItem(`exportWaiting.${payload.merchantId}`, localStorage.getItem(`exportWaiting.${payload.merchantId}`) + `,${e.file_download_url}`);
  //         } else {
  //           localStorage.setItem(`exportWaiting.${payload.merchantId}`, e.file_download_url);
  //         }

  //         const toastBodyElements = [];
  //         toastBodyElements.push(
  //           window.createElement(
  //             'p',
  //             { class: ['text-success'] },
  //             [
  //               window.createElement('b-icon-check', { class: ['mr-2'] }),
  //               `${e.added_contacts} contacts exported`,
  //             ],
  //           ),
  //         );
  //         toastBodyElements.push(
  //           window.createElement(
  //             'p',
  //             [
  //               'Click on the button below to download contacts export file.',
  //               window.createElement(
  //                 'a',
  //                 {
  //                   attrs: {
  //                     href: e.file_download_url,
  //                     target: '_blank',
  //                     'aria-label': 'Close',
  //                   },
  //                   class: ['link', 'btn'],
  //                   on: {
  //                     click: () => removeFromExportList(payload.exportUUID, e.file_download_url, payload.merchantId),
  //                   },
  //                 },
  //                 'Download file',
  //               ),
  //             ],
  //           ),
  //         );
  //         const toastMessage = window.createElement(
  //           'p',
  //           { class: ['mb-0'] },
  //           toastBodyElements,
  //         );
  //         window.sbToast.toast([toastMessage], {
  //           id: `export-toast-${payload.exportUUID}`,
  //           title: 'Contact export',
  //           toaster: 'b-toaster-top-right',
  //           noAutoHide: true,
  //         });
  //       }
  //     });
  // },
  // unsubscribeFromExportStats({ dispatch }, payload) {
  //   localStorage.removeItem(`exportUUID.${payload.merchantId}`);
  //   connectEcho();
  //   window.echo.leave(`contact_export.${payload.exportUUID}`);
  //   dispatch('changeExportRunningState', false);
  // },
  // clearExportStats({ commit }) {
  //   commit('CLEAR_EXPORT_STATS');
  // },
  // changeExportRunningState({ commit }, payload) {
  //   commit('CHANGE_EXPORT_RUNNING_STATE', payload);
  // },
  // clearExportInactivityTimeut() {
  //   clearTimeout(exportInactivityTimeout);
  // },

  // POST ORGANISATION PUSHER CODE - TODO everithing above this line will have to be removed as it is being rewritten
  // definitions - https://github.com/sendbee/sendbee-backend/issues/1502#issuecomment-872984451
  joinOrganisationChannel({ commit, dispatch, rootState, rootGetters }, organisationId) {
    const permissionDenied = rootGetters['user/permissionDenied'];
    if (permissionDenied.message === 'Unauthorized IP - Access Denied') {
      return;
    }

    connectPusher(rootState);

    const channelName = rootState.system.pusherChannels.data.replace('{organisation_id}', organisationId);
    let initialized = false;

    // Do not make additional event triggers if channel has already been subscribed
    if (channelIsConnected(channelName) && window.pusher.channels.channels[channelName].subscribed) {
      return;
    }

    window.pusher.connection.bind('connecting', () => { });
    window.pusher.connection.bind('unavailable', () => {
      if (commit) {
        commit('ECHO_CONNECTION_UNAVAILABLE', true);
      }
    });
    window.pusher.connection.bind('connected', () => {
      // console.log('p - c', initialized);
      if (initialized) {
        setTimeout(() => {
          Object.keys(window.pusher.channels.channels).forEach((channel) => {
            if (!window.pusher.channels.channels[channel].subscribed) {
              dispatch('reconnectChannel', channel);
            } else {
              // clear all cache
              dispatch('chat/clearFastReload', null, { root: true });
              // TODO reinitialize app but silently

              // Also reload first page of the current chat
              dispatch('chat/reloadFirstPage', true, { root: true });
            }
          });
          // One of the three options to detect when to resync the app. #resync
          commit('RECONNECT_CHANNEL');

          // if (this.reconnectChannel > 1) {
          //   const currentOrganisation = rootGetters['organisation/currentOrganisation'];
          //   dispatch(
          //     'organisation/fetchOrganisationStatistics',
          //     { organisationId: currentOrganisation.id, forceStatistics: true, data: { sections: 'all_unread,user_unread' } },
          //     { root: true },
          //   );
          // }
        }, 1000);
      } else {
        initialized = true;
      }

      if (commit) {
        commit('ECHO_CONNECTION_UNAVAILABLE', false);
      }
    });
    window.pusher.connection.bind('disconnected', () => {
      if (commit) {
        commit('ECHO_CONNECTION_UNAVAILABLE', true);
      }
    });
    window.pusher.connection.bind('error', () => {
      if (commit) {
        commit('ECHO_CONNECTION_UNAVAILABLE', true);
      }
    });
    window.pusher.connection.bind('message', (message) => {
      if (commit) {
        commit('MARK_LAST_MESSAGE_TIME');
      }
    });

    const channel = window.pusher.subscribe(channelName);

    channel.bind('subscription_error', () => { });

    channel.bind_global(() => { });

    // Messages
    // --------------------------------
    // ✅ Done
    channel.bind('permissions_changed', () => {
      dispatch('user/fetchUser', null, { root: true });
    });
    // ✅ Done
    channel.bind('inbound_message_received', (data) => {
      if (userHasPermissionForConversation(data, rootState, rootGetters)) {
        if (rootState.chat.chatFastReload[data.conversation_uid] && rootState.chat.chatFastReload[data.conversation_uid].data.find(message => message.id === data.message.sid || message.id === data.data.id)) {
          return;
        }

        commit('NEW_MESSAGE_RECEIVED', data);
        dispatch('chat/addMessageToFastLoad', data, { root: true });

        if (data.message.direction === 'inbound') {
          const statisticLocalData = rootGetters['organisation/statisticLocalData'];
          const me = rootGetters['organisation/me'];
          const currentOrganisation = rootGetters['organisation/currentOrganisation'];
          if (statisticLocalData[data.business_uid] && statisticLocalData[data.business_uid][data.conversation_uid] >= 0) {
            const statisticPayload = {
              businessId: data.business_uid,
              conversationId: data.conversation_uid,
              unseenMessages: statisticLocalData[data.business_uid][data.conversation_uid] + 1,
              myConversation: data.assigned_user_id === me.id,
              myId: me.id,
            };

            dispatch('organisation/updateStatisticLocalData', statisticPayload, { root: true });
          } else {
            dispatch(
              'chat/fetchSingleConversation',
              {
                businessId: data.business_uid,
                conversationId: data.conversation_uid,
              },
              { root: true },
            )
              .then((conversation) => {
                if (conversation.data.message_unseen_count > 0) { // This is a special situation when we can't know if the unassigned agent has this counted already or not so we need to sync with backend
                  dispatch(
                    'organisation/fetchOrganisationNumberStatistics',
                    {
                      organisationId: currentOrganisation.id,
                      businessId: conversation.data.business_id,
                      data: {
                        sections: 'all_unread,user_unread',
                      },
                      forceStatistics: true,
                    },
                    { root: true },
                  );
                }

                const statisticPayload = {
                  businessId: conversation.data.business_id,
                  conversationId: data.conversation_uid,
                  unseenMessages: conversation.data.message_unseen_count,
                  myConversation: conversation.data.user_id === me.id,
                  myId: me.id,
                };

                dispatch('organisation/updateStatisticLocalData', statisticPayload, { root: true });
              });
          }
        }

        // reset consent to true if inbound message
        dispatch('chat/enableConsent', data, { root: true });
        data.activity = {
          conversation_id: data.conversation_uid,
          meta_data: {
            new_folder: 'open',
            old_folder: 'done',
          },
        };

        handleConsent(dispatch, rootGetters, organisationId, data, true);
        handleFolderChange(commit, dispatch, rootState, rootGetters, organisationId, data);
      }
    });
    // ✅ Done
    channel.bind('outbound_message_sent', (data) => { // TODO pusher - this one doesnt trigger - Ivan is fixing it - 4.4.2022
      if (userHasPermissionForConversation(data, rootState, rootGetters)) {
        // commit('SENT_MESSAGE_STATUS_CHANGED', data);
        // add message to message list
        commit('NEW_MESSAGE_RECEIVED', data);
      }
    });
    // ✅ Done
    channel.bind('outbound_message_status', (data) => {
      if (userHasPermissionForConversation(data, rootState, rootGetters)) {
        commit('SENT_MESSAGE_STATUS_CHANGED', data);
      }
    });

    // Conversations
    // --------------------------------
    channel.bind('conversation_read_status_changed', (data) => {
      const myConversation = data.conversation.assigned_user_id === rootGetters['organisation/me'].id;
      if (
        myConversation && data.conversation && !data.conversation.assignment_seen
      ) {
        return;
      }
      rootState.chat.conversationSeen = data;
      // TODO fetching and setting statistcs after the conversation list is updated, creates a little delay. They should both be updated at the same time.
      // dispatch('organisation/fetchOrganisationNumberStatistics', { organisationId, businessId: data.business_uid, data: { sections: 'all_unread,user_unread' } }, { root: true }); // NOTE: We decided not to update statistics here but it is left here in case we change something.
      commit('CONVERSATION_READ_UPDATE', data);
    });

    // ✅ Done
    channel.bind('conversation_activity', (data) => {
      commit('NEW_CONVERSATION_ACTIVITY', data);
      if (data.activity.action === 'messaging_consent_disabled') {
        dispatch('chat/disableConsent', data, { root: true });
      }

      // Update conversation cache data
      switch (data.activity.action) {
        case 'folder': {
          handleFolderChange(commit, dispatch, rootState, rootGetters, organisationId, data);
          break;
        }
        case 'assignment': {
          handleAssignmentActivity(dispatch, rootGetters, organisationId, data);
          break;
        }
        case 'Block': {
          handleBlockedActivity(dispatch, rootGetters, organisationId, data, true);
          break;
        }
        case 'Unblock': {
          handleBlockedActivity(dispatch, rootGetters, organisationId, data, false);
          break;
        }
        case 'messaging_consent_disabled':
          handleConsent(dispatch, rootGetters, organisationId, data, false);
          break;
        default:
          break;
      }
    });

    // Contacts
    // --------------------------------
    channel.bind('contacts_import_progress', (data) => {
      // https://github.com/sendbee/sendbee-backend/issues/1502#issuecomment-872984451
    });

    channel.bind('contacts_export_progress', (data) => {
      // https://github.com/sendbee/sendbee-backend/issues/1502#issuecomment-872984451
    });

    channel.bind('contact_created', (data) => {
      dispatch('contactDetails/contactAdded', data, { root: true });
      dispatch('contact/resyncContactCache', null, { root: true });
    });

    channel.bind('contact_deleted', (data) => {
      dispatch('contactDetails/contactDeleted', data, { root: true });
      dispatch('contact/resyncContactCache', null, { root: true });
    });

    channel.bind('contact_changed', (data) => {
      // For chat
      const activeDetailsSection = rootGetters['contactDetails/activeDetailsSection'];

      // For contacts table
      const activeSection = rootGetters['contactDetails/activeSection'];
      const contactData = rootGetters['contactDetails/contactData'];

      if (
        (activeSection === 'info' && contactData && contactData.id === data.contact_uid) ||
        (activeDetailsSection === 'info' && contactData && contactData.id === data.contact_uid)
      ) {
        dispatch('contactDetails/contactUpdated', data, { root: true });
        // dispatch('contact/resyncContactCache', null, { root: true });
      }
    });

    // Broadcast
    // --------------------------------
    channel.bind('bulk_send_progress', (data) => {
      // https://github.com/sendbee/sendbee-backend/issues/1502#issuecomment-872984451
    });

    // Quick Replies
    // --------------------------------
    channel.bind('quick_reply_created', (data) => {
      dispatch('chat/addQuickReply', data, { root: true });
    });
    channel.bind('quick_reply_changed', (data) => {
      dispatch('chat/editQuickReply', data, { root: true });
    });
    channel.bind('quick_reply_deleted', (data) => {
      dispatch('chat/deleteSingleQuickReply', data.quick_reply_uid, { root: true });
    });

    // Custom fields
    // --------------------------------
    channel.bind('custom_field_created', (data) => {
      dispatch('contact/addCustomField', data, { root: true });
    });
    channel.bind('custom_field_changed', (data) => {
      dispatch('contact/editCustomField', data, { root: true });
    });
    channel.bind('custom_field_deleted', (data) => {
      dispatch('contact/deleteSingleCustomField', data.custom_field_uid, { root: true });
      dispatch('contact/deletedCustomField', data, { root: true });
    });

    // Number
    // --------------------------------
    channel.bind('business_availability_changed', (data) => {
      commit('SET_BUSINESS_AVAILABILITY', data);
    });

    channel.bind('business_changed', (data) => {
      dispatch('organisation/numberChanged', data, { root: true });
    });

    channel.bind('organisation_changed', (data) => {
      dispatch('organisation/organisationChanged', data, { root: true });
    });

    // User
    // --------------------------------
    channel.bind('user_availability_changed', (data) => {
      data.organisationId = organisationId;
      // TODO
      commit('SET_USER_AVAILABILITY', data.available);
    });

    channel.bind('user_removed_from_organisation', (data) => {
      dispatch('merchant/memberRemovedFromOrganisation', data, { root: true });
      dispatch('organisation/memberRemovedFromOrganisation', data, { root: true });
      window.eventBus.$emit('contact-deleted', data);
    });

    channel.bind('user_added_to_business', (data) => {
      clearTimeout(loadUserDataTimeout); // timeout for user settings because when user role is changed we get as many updates as there are numbers, and we do not want to triget this logic for all of them but only for the last one.
      loadUserDataTimeout = setTimeout(() => {
        dispatch('user/fetchUser', null, { root: true });
      }, 1000);
      dispatch('organisation/addMemberToBusiness', data, { root: true });
      dispatch('joinNumberChannel', data.business_uid);
    });

    channel.bind('user_removed_from_business', (data) => {
      clearTimeout(loadUserDataTimeout); // timeout for user settings because when user role is changed we get as many updates as there are numbers, and we do not want to triget this logic for all of them but only for the last one.
      loadUserDataTimeout = setTimeout(() => {
        dispatch('user/fetchUser', null, { root: true });
      }, 1000);
      dispatch('organisation/removeMemberFormBusiness', data, { root: true });
      dispatch('leaveNumberChannel', data.business_uid);
    });

    // Similar to user_removed_from_organisation in terms of removing the date from the members table
    channel.bind('user_invitation_removed', (data) => {
      dispatch('merchant/memberRemovedFromOrganisation', { user_uid: data.invitation_uid }, { root: true });
    });

    channel.bind('user_invitation_created', (data) => {
      // emit and reload from component
      window.eventBus.$emit('contact-invitation-created', data);
    });

    channel.bind('user_account_activated', (data) => {
      window.eventBus.$emit('user-activation-action', data);
    });

    // System
    channel.bind('system_message_web_app', (data) => {
      switch (data.action) {
        case 'fetch_sett':
          dispatch('system/fetchSettings', null, { root: true });
          break;
        case 'restart':
          window.eventBus.$emit('restart', data);
          break;
        case 'notice':
          window.eventBus.$emit('notice', data);
          break;
        default:
          break;
      }
      dispatch('system/newMessage', null, { root: true });
    });

    // Automated messages changed
    channel.bind('automated_messages_changed', (data) => {
      // data.changed_messages.forEach(replyId => dispatch('automation/fetchInstantReply', { replyId, businessId: data.business_uid }, { root: true }));
      // dispatch('automation/fetchInstantReplyBatch', data);
    });

    // Notes
    channel.bind('contact_note_changed', (data) => {
      dispatch('contactDetails/silentlyUpdateNotes', data, { root: true });
    });

    // Billing
    channel.bind('payment_successful', (data) => {
      dispatch('organisation/paymentSuccessful', data, { root: true });
    });

    channel.bind('payment_failed', (data) => {
      dispatch('organisation/paymentFailed', data, { root: true });
    });

    // Bot
    channel.bind('bot_on_off', (data) => {
      dispatch('chat/updateBotStatusForConversation',
        {
          businessId: data.business_id,
          conversationId: data.conversation_id,
          data: {
            bot_active: data.state === 'on',
          },
        },
        { root: true });
    });

    // BROADCAST
    // Received for live_inbox = false so not all conversations need to be updated. Only the ones already on the message list.
    channel.bind('broadcast_send_progress', (data) => {
      if (rootState.organisation.progressActions && rootState.organisation.progressActions[data.broadcast_uid] && rootState.organisation.progressActions[data.broadcast_uid].state === ProgressStates.FINISHED) return; // Sometimes data comes after the finish event so just ignore it.
      let progress = data.progress.total ? Math.floor(data.progress.sent / data.progress.total * 100) : 0;
      // Update progress only for user that sent the broadcast
      if (rootState.organisation.progressActions && rootState.organisation.progressActions[data.broadcast_uid]) {
        if (progress < rootState.organisation.progressActions[data.broadcast_uid].progress) {
          // Progress will continue with new stats data but to be sure progress doesn't goes back because of some late message, do not allow progress to become smaller than previous one.
          progress = rootState.organisation.progressActions[data.broadcast_uid].progress;
        }
        let addToSend = 0;
        // These params from failed are also counted into sent as Ivan requested 23.5.2023 12:18
        ['contact_unsubscribed', 'invalid_phone', 'contact_blocked'].forEach((key) => { addToSend += Number(data.progress.failed[key]) || 0; });

        // Sometimes progress event gets late and numbers seem to go backward which we need to prevent. This is why we keep track of the minimum number of done items.
        let minDone = 0;
        try {
          minDone = rootState.organisation.progressActions[data.broadcast_uid].minDone > (Number(data.progress.sent) + addToSend) ? rootState.organisation.progressActions[data.broadcast_uid].minDone : Number(data.progress.sent) + addToSend;
        } catch (error) { }

        const progressData = {
          progressId: data.broadcast_uid,
          stats: data.progress,
          data: {
            // subtitle: `${window.vueFilters.bigDigits(Number(data.progress.sent) + addToSend)} of ${window.vueFilters.bigDigits(data.progress.total)}`,
            subtitle: `${window.vueFilters.bigDigits(minDone)} of ${window.vueFilters.bigDigits(data.progress.total)}`,
            progress,
            state: rootState.organisation.progressActions[data.broadcast_uid].state !== ProgressStates.PAUSED ? ProgressStates.RUNNING : ProgressStates.PAUSED, // if paused then it must remain paused when late progress arrives
            stats: data.progress,
            minDone,
          },
        };
        if (progress === 100) {
          progressData.data.state = ProgressStates.FINISHED;
          progressData.data.title = 'Broadcast sent';
        }
        dispatch('organisation/updateProgressAction', progressData, { root: true });
      }

      // Update conversations for all users
      dispatch('chat/updateConversations', { businessId: data.business_uid, conversations: data.conversations }, { root: true });
    });
  },

  // TODO on logout or org change
  leaveOrganisationChannel({ rootState }, organisationId) {
    const channelName = rootState.system && rootState.system.pusherChannels && rootState.system.pusherChannels.data ? rootState.system.pusherChannels.data.replace('{organisation_id}', organisationId) : null;
    if (channelName) {
      window.pusher.unsubscribe(channelName);
    }
  },

  // --------------------------------------------------------------- //
  //                                                                 //
  // BUSINESS CHANNEL                                                //
  //                                                                 //
  // --------------------------------------------------------------- //
  joinNumberChannel({
    rootState,
    dispatch,
    rootGetters,
    commit,
  }, numberId) {
    const permissionDenied = rootGetters['user/permissionDenied'];
    if (permissionDenied.message === 'Unauthorized IP - Access Denied') {
      return;
    }

    connectPusher(rootState);

    const channelName = rootState.system.pusherChannels['business-data'].replace('{business_id}', numberId);
    let initialized = false;

    // Do not make additional event triggers if channel has already been subscribed
    if (channelIsConnected(channelName) && window.pusher.channels.channels[channelName].subscribed) {
      return;
    }

    window.pusher.connection.bind('connecting', () => { });
    window.pusher.connection.bind('unavailable', () => {
      if (commit) {
        commit('ECHO_CONNECTION_UNAVAILABLE', true);
      }
    });
    window.pusher.connection.bind('connected', () => {
      if (initialized) {
        setTimeout(() => {
          Object.keys(window.pusher.channels.channels).forEach((channel) => {
            if (!window.pusher.channels.channels[channel].subscribed) {
              dispatch('reconnectChannel', channel);
            } else {
              // clear all cache
              dispatch('chat/clearFastReload', null, { root: true });         // ROMOVE FROM ORG
              // TODO reinitialize app but silently

              // Also reload first page of the current chat
              dispatch('chat/reloadFirstPage', true, { root: true });        // ROMOVE FROM ORG
            }
          });
          // One of the three options to detect when to resync the app. #resync
          commit('RECONNECT_CHANNEL');

          // if (this.reconnectChannel > 1) {
          //   const currentOrganisation = rootGetters['organisation/currentOrganisation'];
          //   dispatch(
          //     'organisation/fetchOrganisationStatistics',
          //     { organisationId: currentOrganisation.id, forceStatistics: true, data: { sections: 'all_unread,user_unread' } },
          //     { root: true },
          //   );
          // }
        }, 1000);
      } else {
        initialized = true;
      }

      if (commit) {
        commit('ECHO_CONNECTION_UNAVAILABLE', false);
      }
    });
    window.pusher.connection.bind('disconnected', () => {
      if (commit) {
        commit('ECHO_CONNECTION_UNAVAILABLE', true);
      }
    });
    window.pusher.connection.bind('error', () => {
      if (commit) {
        commit('ECHO_CONNECTION_UNAVAILABLE', true);
      }
    });
    window.pusher.connection.bind('message', (message) => {
      if (commit) {
        commit('MARK_LAST_MESSAGE_TIME');
      }
    });

    const channel = window.pusher.subscribe(channelName);

    // Messages
    // --------------------------------
    channel.bind('inbound_message_received', (data) => {
      if (userHasPermissionForConversation(data, rootState, rootGetters)) {
        if (rootState.chat.chatFastReload[data.conversation_uid] && rootState.chat.chatFastReload[data.conversation_uid].data.find(message => message.id === data.message.sid || message.id === data.data.id)) {
          return;
        }

        commit('NEW_MESSAGE_RECEIVED', data);
        dispatch('chat/addMessageToFastLoad', data, { root: true });

        if (data.message.direction === 'inbound') {
          const statisticLocalData = rootGetters['organisation/statisticLocalData'];
          const me = rootGetters['organisation/me'];
          const currentOrganisation = rootGetters['organisation/currentOrganisation'];
          if (statisticLocalData[data.business_uid] && statisticLocalData[data.business_uid][data.conversation_uid] >= 0) {
            const statisticPayload = {
              businessId: data.business_uid,
              conversationId: data.conversation_uid,
              unseenMessages: statisticLocalData[data.business_uid][data.conversation_uid] + 1,
              myConversation: data.assigned_user_id === me.id,
              myId: me.id,
            };

            dispatch('organisation/updateStatisticLocalData', statisticPayload, { root: true });
          } else {
            dispatch(
              'chat/fetchSingleConversation',
              {
                businessId: data.business_uid,
                conversationId: data.conversation_uid,
                data: {
                  noCancel: true,
                },
              },
              { root: true },
            )
              .then((conversation) => {
                if (conversation.data.message_unseen_count > 0) { // This is a special situation when we can't know if the unassigned agent has this counted already or not so we need to sync with backend
                  dispatch(
                    'organisation/fetchOrganisationNumberStatistics',
                    {
                      organisationId: currentOrganisation.id,
                      businessId: conversation.data.business_id,
                      data: {
                        sections: 'all_unread,user_unread',
                        noCancel: true,
                      },
                      forceStatistics: true,
                    },
                    { root: true },
                  );
                }

                const statisticPayload = {
                  businessId: conversation.data.business_id,
                  conversationId: data.conversation_uid,
                  unseenMessages: conversation.data.message_unseen_count,
                  myConversation: conversation.data.user_id === me.id,
                  myId: me.id,
                };

                dispatch('organisation/updateStatisticLocalData', statisticPayload, { root: true });
              });
          }
        }

        // reset consent to true if inbound message
        dispatch('chat/enableConsent', data, { root: true });
        data.activity = {
          conversation_id: data.conversation_uid,
          meta_data: {
            new_folder: 'open',
            old_folder: 'done',
          },
        };

        handleConsent(dispatch, rootGetters, rootGetters['organisation/currentOrganisation'].id, data, true);
        handleFolderChange(commit, dispatch, rootState, rootGetters, rootGetters['organisation/currentOrganisation'].id, data);
      }
    });
    channel.bind('outbound_message_sent', (data) => { // TODO pusher - this one doesnt trigger - Ivan is fixing it - 4.4.2022
      if (userHasPermissionForConversation(data, rootState, rootGetters)) {
        // commit('SENT_MESSAGE_STATUS_CHANGED', data);
        // add message to message list
        commit('NEW_MESSAGE_RECEIVED', data);
      }
    });
    channel.bind('outbound_message_status', (data) => {
      if (userHasPermissionForConversation(data, rootState, rootGetters)) {
        commit('SENT_MESSAGE_STATUS_CHANGED', data);
      }
    });

    // Conversations
    // --------------------------------
    channel.bind('conversation_read_status_changed', (data) => {
      // const myConversation = data.conversation.assigned_user_id === rootGetters['organisation/me'].id;
      // if (
      //   myConversation && data.conversation && !data.conversation.assignment_seen
      // ) {
      //   return;
      // }
      rootState.chat.conversationSeen = data;
      // TODO fetching and setting statistcs after the conversation list is updated, creates a little delay. They should both be updated at the same time.
      // dispatch('organisation/fetchOrganisationNumberStatistics', { organisationId, businessId: data.business_uid, data: { sections: 'all_unread,user_unread' } }, { root: true }); // NOTE: We decided not to update statistics here but it is left here in case we change something.
      commit('CONVERSATION_READ_UPDATE', data);
      commit('chat/ACTIVE_CONVERSATION_READ_UPDATE', data, { root: true });
    });

    channel.bind('conversation_activity', (data) => {
      commit('NEW_CONVERSATION_ACTIVITY', data);
      if (data.activity.action === 'messaging_consent_disabled') {
        dispatch('chat/disableConsent', data, { root: true });
      }

      // Update conversation cache data
      switch (data.activity.action) {
        case 'folder': {
          handleFolderChange(commit, dispatch, rootState, rootGetters, rootGetters['organisation/currentOrganisation'].id, data);
          break;
        }
        case 'assignment': {
          handleAssignmentActivity(dispatch, rootGetters, rootGetters['organisation/currentOrganisation'].id, data);
          break;
        }
        case 'Block': {
          handleBlockedActivity(dispatch, rootGetters, rootGetters['organisation/currentOrganisation'].id, data, true);
          break;
        }
        case 'Unblock': {
          handleBlockedActivity(dispatch, rootGetters, rootGetters['organisation/currentOrganisation'].id, data, false);
          break;
        }
        case 'messaging_consent_disabled':
          handleConsent(dispatch, rootGetters, rootGetters['organisation/currentOrganisation'].id, data, false);
          break;
        default:
          break;
      }
    });

    // Bot
    channel.bind('bot_on_off', (data) => {
      dispatch('chat/updateBotStatusForConversation',
        {
          businessId: data.business_id,
          conversationId: data.conversation_id,
          data: {
            bot_active: data.state === 'on',
          },
        },
        { root: true });
    });

    channel.bind('inbound_reaction_received', (data) => {
      dispatch('chat/emojiReactionReceived',
        {
          businessId: data.business_uid,
          conversationId: data.conversation_uid,
          direction: 'inbound',
          ...data.data,
        },
        { root: true });
    });
    channel.bind('outbound_reaction_sent', (data) => {
      dispatch('chat/emojiReactionReceived',
        {
          businessId: data.business_uid,
          conversationId: data.conversation_uid,
          direction: 'outbound',
          ...data.data,
        },
        { root: true });
    });
  },

  leaveNumberChannel({ rootState }, numberId) {
    const channelName = rootState.system && rootState.system.pusherChannels && rootState.system.pusherChannels.data ? rootState.system.pusherChannels['business-data'].replace('{business_id}', numberId) : null;
    if (channelName) {
      window.pusher.unsubscribe(channelName);
    }
  },

  // TODO pusher private channel
  joinPrivateChannel({ rootState, dispatch, commit }) {
    connectPusher(rootState);

    const channelName = rootState.system && rootState.system.pusherChannels && rootState.system.pusherChannels.private ? rootState.system.pusherChannels.private.replace('{user_id}', rootState.user.id) : null;
    const ch = window.pusher.subscribe(channelName);

    ch.bind('transfer_ownership', () => {
      dispatch('organisation/fetchOrganisationMembers', rootState.organisation.currentOrganisation.id, { root: true });
    });

    ch.bind('sent_message_reference', (data) => {
      dispatch('chat/updateMessageSid', {
        sid: data.message_id,
        temp_sid: data.message_reference_id,
        conversation_id: data.conversation_uid,
        message: data.message,
      }, { root: true });
    });

    ch.bind('sent_message_validation_fail', (data) => {
      commit('SENT_MESSAGE_STATUS_CHANGED', {
        message: {
          sid: data.message_reference_id,
          id: data.message_reference_id,
          status: 'failed',
        },
        error_message: data.error_message,
        conversation_uid: data.conversation_uid,
      });
    });

    ch.bind('create_whatsapp_template_success', (data) => {
      commit('notification/TEMPLATE_MESSAGE_VALIDATION_SUCCESS', data, { root: true });
    });

    ch.bind('create_whatsapp_template_validation_fail', (data) => {
      commit('notification/TEMPLATE_MESSAGE_VALIDATION_FAIL', data, { root: true });
    });

    ch.bind('contacts_import_progress', (data) => {
      if (rootState.organisation.progressActions[data.import_uid].state === ProgressStates.FINISHED) return; // Sometimes data comes after the finish event so just ignore it.

      const progress = data.progress.total_contacts ? Math.floor((data.progress.added_contacts + data.progress.duplicated_contacts + data.progress.failed_contacts.invalid_phone_format + data.progress.failed_contacts.other) / data.progress.total_contacts * 100) : 0;
      if (progress < rootState.organisation.progressActions[data.import_uid].progress) {
        return; // This is in case some older progress info comes after the newer one. eg. set progres to 30% and previously it was already set to 50%.
      }

      // Sometimes progress event gets late and numbers seem to go backward which we need to prevent. This is why we keep track of the minimum number of done items.
      let minDone = 0;
      try {
        minDone = rootState.organisation.progressActions[data.import_uid].minDone > (data.progress.added_contacts + data.progress.duplicated_contacts + data.progress.failed_contacts.invalid_phone_format + data.progress.failed_contacts.other) ? rootState.organisation.progressActions[data.import_uid].minDone : data.progress.added_contacts + data.progress.duplicated_contacts + data.progress.failed_contacts.invalid_phone_format + data.progress.failed_contacts.other;
      } catch (error) { }

      const progressData = {
        progressId: data.import_uid,
        stats: data.progress,
        data: {
          subtitle: `${window.vueFilters.bigDigits(minDone)} of ${window.vueFilters.bigDigits(data.progress.total_contacts)}`,
          progress,
          state: ProgressStates.RUNNING,
          minDone,
        },
      };
      if (progress === 100) {
        progressData.data.state = ProgressStates.FINISHED;
        progressData.data.title = 'Contacts imported';
      }
      dispatch('organisation/updateProgressAction', progressData, { root: true });
    });

    ch.bind('contacts_import_finished', (data) => {
      // const progress = Math.floor((data.progress.added_contacts + data.progress.duplicated_contacts + data.progress.failed_contacts.invalid_phone_format + data.progress.failed_contacts.other) / data.progress.total_contacts * 100);
      // if (progress < rootState.organisation.progressActions[data.import_uid].progress) {
      //   return; // This is in case some older progress info comes after the newer one. eg. set progres to 30% and previously it was already set to 50%.
      // }
      const progress = 100;
      const progressData = {
        progressId: data.import_uid,
        stats: data.progress,
        data: {
          title: 'Contacts imported',
          subtitle: `${window.vueFilters.bigDigits(data.progress.total_contacts)} of ${window.vueFilters.bigDigits(data.progress.total_contacts)}`,
          progress,
          state: ProgressStates.FINISHED,
          stats: data.progress,
          minDone: data.progress.total_contacts,
        },
      };

      window.eventBus.$emit('import-finished');
      window.eventBus.$emit('show-toast', { message: 'Contacts have been successfully imported.', variant: 'OK' });
      dispatch('organisation/updateProgressAction', progressData, { root: true });
    });

    ch.bind('contacts_import_paused', (data) => {
      const progressData = {
        progressId: data.import_uid,
        data: {
          title: 'Importing paused',
          state: ProgressStates.PAUSED,
        },
      };

      dispatch('organisation/updateProgressAction', progressData, { root: true });
    });

    ch.bind('contacts_import_aborted', (data) => {
      const progressData = {
        progressId: data.import_uid,
        data: {
          title: 'Importing paused',
          subtitle: 'Something went wrong',
          state: ProgressStates.FAILED,
        },
      };

      window.eventBus.$emit('show-toast', { message: 'Contact import has been aborted', variant: 'error' });
      dispatch('organisation/updateProgressAction', progressData, { root: true });
    });

    // BROADCAST
    // Received for live_inbox = true so sender can read the stats. No need to update anything since regular messaging event will be triggered.
    ch.bind('broadcast_send_progress', (data) => {
      if (
        !rootState.organisation.progressActions[data.broadcast_uid] ||
        rootState.organisation.progressActions[data.broadcast_uid].state === ProgressStates.FINISHED // Sometimes data comes after the finish event so just ignore it.
      ) {
        return;
      }

      let addToSend = 0;
      // These params from failed are also counted into sent as Ivan requested 23.5.2023 12:18
      ['contact_unsubscribed', 'invalid_phone', 'contact_blocked'].forEach((key) => { addToSend += Number(data.progress.failed[key]) || 0; });

      let progress = data.progress.total ? Math.floor(data.progress.sent / data.progress.total * 100) : 0;
      if (progress < rootState.organisation.progressActions[data.broadcast_uid].progress) {
        // Progress will continue with new stats data but to be sure progress doesn't goes back because of some late message, do not allow progress to become smaller than previous one.
        progress = rootState.organisation.progressActions[data.broadcast_uid].progress;
      }
      // Sometimes progress event gets late and numbers seem to go backward which we need to prevent. This is why we keep track of the minimum number of done items.
      let minDone = 0;
      try {
        minDone = rootState.organisation.progressActions[data.broadcast_uid].minDone > (Number(data.progress.sent) + addToSend) ? rootState.organisation.progressActions[data.broadcast_uid].minDone : Number(data.progress.sent) + addToSend;
      } catch (error) { }

      const progressData = {
        progressId: data.broadcast_uid,
        stats: data.progress,
        data: {
          subtitle: `${window.vueFilters.bigDigits(minDone)} of ${window.vueFilters.bigDigits(data.progress.total)}`,
          progress,
          state: rootState.organisation.progressActions[data.broadcast_uid].state !== ProgressStates.PAUSED ? ProgressStates.RUNNING : ProgressStates.PAUSED, // if paused then it must remain paused when late progress arrives
          stats: data.progress,
          minDone,
        },
      };
      if (progress === 100) {
        progressData.data.state = ProgressStates.FINISHED;
        progressData.data.title = 'Broadcast sent';
      }
      dispatch('organisation/updateProgressAction', progressData, { root: true });
    });
    ch.bind('broadcast_paused', (data) => {
      const progressData = {
        progressId: data.broadcast_uid,
        data: {
          title: 'Broadcast paused',
          state: ProgressStates.PAUSED,
        },
      };

      dispatch('organisation/updateProgressAction', progressData, { root: true });
    });
    ch.bind('broadcast_unpaused', (data) => {
      // console.log('Broadcast unpaused', data);
    });
    ch.bind('broadcast_aborted', (data) => {
      const progressData = {
        progressId: data.broadcast_uid,
        data: {
          title: 'Broadcast paused',
          subtitle: 'Something went wrong',
          state: ProgressStates.FAILED,
        },
      };

      window.eventBus.$emit('show-toast', { message: 'Broadcast has been aborted', variant: 'error' });
      dispatch('organisation/updateProgressAction', progressData, { root: true });
    });
    ch.bind('broadcast_finished', (data) => {
      // let progress = data.progress.total ? Math.floor(data.progress.sent / data.progress.total * 100) : 0;
      // if (progress < rootState.organisation.progressActions[data.broadcast_uid].progress) {
      //   // Progress will continue with new stats data but to be sure progress doesn't goes back because of some late message, do not allow progress to become smaller than previous one.
      //   progress = rootState.organisation.progressActions[data.broadcast_uid].progress;
      // }
      const progress = 100;
      const progressData = {
        progressId: data.broadcast_uid,
        stats: data.progress,
        data: {
          title: 'Broadcast sent',
          subtitle: `${window.vueFilters.bigDigits(data.progress.total)} of ${window.vueFilters.bigDigits(data.progress.total)}`,
          progress,
          state: ProgressStates.FINISHED,
          // stats: data.progress,
          minDone: data.progress.total,
        },
      };

      window.eventBus.$emit('show-toast', { message: 'Broadcast has been successfully sent.', variant: 'OK' });
      dispatch('organisation/updateProgressAction', progressData, { root: true });
    });
  },
  leavePrivateChannel() { },

  joinFocusChannel({ rootState, rootGetters, dispatch }, payload) {
    const permissionDenied = rootGetters['user/permissionDenied'];
    if (permissionDenied.message === 'Unauthorized IP - Access Denied') {
      return;
    }

    connectPusher(rootState);

    const previousChannelName = rootState.system && rootState.system.pusherChannels && rootState.system.pusherChannels.focus ? rootState.system.pusherChannels.focus.replace('{organisation_id}', payload.checkPrevious) : null;

    if (previousChannelName && payload.checkPrevious && channelIsConnected(previousChannelName)) {
      // console.log('OSC'); //OLD STILL CONNECTED
      dispatch('leaveFocusChannel', payload.checkPrevious);
    }

    const channelName = rootState.system && rootState.system.pusherChannels && rootState.system.pusherChannels.focus ? rootState.system.pusherChannels.focus.replace('{organisation_id}', payload.join) : null;

    if (channelName && payload.join) { // && !channelIsConnected(channelName)
      const ch = window.pusher.subscribe(channelName);
      ch.bind('pusher:subscription_error', () => {
        // console.log('FAILEDDDDDDD 2');
        // setTimeout(() => {
        //   this.joinFocusChannel(payload);
        // }, 1000);
      });

      // ch.bind_global((e) => {
      //   console.log('event channel', e);
      // });
    }
  },

  leaveFocusChannel({ rootState }, organisationId) {
    if (!organisationId) {
      return;
    }

    connectPusher(rootState);

    const channelName = rootState.system && rootState.system.pusherChannels && rootState.system.pusherChannels.focus ? rootState.system.pusherChannels.focus.replace('{organisation_id}', organisationId) : null;

    if (channelName && organisationId && channelIsConnected(channelName)) {
      window.pusher.unsubscribe(channelName);
    }
  },

  // cache actions for user which initiated it because he will not receive pusher event so it has to be handled manually
  // joinOrganisationChannel({ commit, dispatch, rootState, rootGetters }, organisationId) {}

  joinDesktopPrivateChannel({ rootState, dispatch, commit }) {
    connectPusher(rootState);

    const channelName = rootState.system && rootState.system.pusherChannels && rootState.system.pusherChannels.desktopPrivate ? rootState.system.pusherChannels.desktopPrivate.replace('{user_id}', rootState.user.platformId) : null;
    if (channelName) {
      const ch = window.pusher.subscribe(channelName);
      ch.bind('pusher:subscription_succeeded', (data) => {
        // console.log('dp connected', data);
      });

      ch.bind('pusher:subscription_error', () => {
        console.log('dp unavailable');
      });

      ch.bind('show_notification', (data) => {
        if (window.todesktop) {
          notification.create({
            title: data.title,
            subtitle: data.subtitle,
            body: data.body,
            image: data.image,
            silent: true,
          }).then((ref) => {
            notification.show({ ref });
            notification.on('click', () => {
              if (data.data) {
                commit('CHANGE_ROUTE', {
                  name: 'OrganisationBusinessConversation',
                  params: {
                    organisationId: data.data.organisation_id,
                    businessId: data.data.business_id,
                    conversationId: data.data.conversation_id,
                  },
                });
              }
            }, { ref });
            const notificationSound = new Audio('/static/audio/whatsapp_web_tone.mp3');
            notificationSound.volume = 0.1;
            notificationSound.play().catch(() => { });
          });
        }
      });
    } else {
      setTimeout(() => {
        dispatch('joinDesktopPrivateChannel');
      }, 1000);
    }
  },

  leaveDesktopPrivateChannel({ rootState }) {
    connectPusher(rootState);
    const channelName = rootState.system && rootState.system.pusherChannels && rootState.system.pusherChannels.desktopPrivate ? rootState.system.pusherChannels.desktopPrivate.replace('{user_id}', rootState.user.platformId) : null;
    window.pusher.unsubscribe(channelName);
  },
};
